import React, { useRef, useState } from 'react';
import axios from 'axios';
import { parseDob, ageFrom, fmtMdy } from '../functions/dob';
import { useHistory } from 'react-router-dom';
import Modal from 'react-modal';

import GuestI from '../interfaces/Guest';
import AllAccessGuestI from '../interfaces/AllAccessGuest';
import User from '../interfaces/User';

import useFee from '../hooks/useFee';
import useDiscount from '../hooks/useDiscount';
import sendEmailToGuest from '../functions/sendEmailToGuest';
import { businessDayStart } from '../functions/businessDay';

import Button from '../components/Button';
import FlexContainer from '../components/FlexContainer';
import UpdateGuestModal from '../components/modals/UpdateGuestModal';
import Header from '../components/Header';

const MIN_ENTRY_AGE = 18;

const UpdateGuestForm = ({
    guest,
    setGuest,
    user,
    setLoading,
}: {
    guest: AllAccessGuestI;
    setGuest: (guest: AllAccessGuestI) => void;
    user: User;
    setLoading: (value: boolean) => void;
}) => {
    const history = useHistory();
    const submittingRef = useRef<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [flashName, setFlashName] = useState<string | null>(null);
    const [overrideOpen, setOverrideOpen] = useState<boolean>(false);
    const [overrideValue, setOverrideValue] = useState<string>('');
    const [feeOverride, setFeeOverride] = useState<number | null>(null);
    const [renewalOpen, setRenewalOpen] = useState<boolean>(false);
    const [renewalStatus, setRenewalStatus] = useState<boolean>(false);

    const feeType: string =
        guest?.type === 'allAccess' ? 'AAEventFee' : 'EventFee';
    const feeDay = businessDayStart().getDay();
    const feeToday = new Date().toLocaleDateString('en-US', {
        weekday: 'long',
    });
    const { fee, loadingFee, setGuestPoints } = useFee(
        feeType,
        guest as GuestI,
        feeDay,
        feeToday
    );
    const { discount } = useDiscount('add', guest?.points || 0);

    const dob = parseDob(guest?.dateOfBirth);
    const age = ageFrom(dob);
    const dobDisplay = fmtMdy(dob);
    const fullName = [guest?.firstName, guest?.lastName]
        .filter(Boolean)
        .join(' ');
    const stageOrName =
        (guest?.stageName as string) || fullName || guest?.guestID || 'Guest';

    const isUnderage = age != null && age < MIN_ENTRY_AGE;
    const ageColor = (() => {
        if (age == null) return '#666';
        if (age < 18) return '#c0392b';
        if (age < 21) return '#e67e22';
        return '#27ae60';
    })();

    type Decision = {
        label: string;
        sub?: string;
        bg: string;
        border: string;
    };
    const decision: Decision = (() => {
        if (age == null) {
            return {
                label: 'DOB UNREADABLE — RESCAN ID',
                bg: '#7f8c8d',
                border: '#34495e',
            };
        }
        if (age < 18) {
            return {
                label: '⛔ DO NOT ADMIT',
                sub: 'Guest is underage.',
                bg: '#c0392b',
                border: '#7b1d12',
            };
        }
        if (age < 21) {
            return {
                label: '⚠️ 18+ BUT UNDER 21',
                sub: 'Do not allow alcohol.',
                bg: '#e67e22',
                border: '#a14d0c',
            };
        }
        return {
            label: '✅ OK TO ADMIT',
            bg: '#27ae60',
            border: '#0f6d38',
        };
    })();

    const effectiveFeeValue =
        feeOverride != null ? feeOverride : fee?.value ?? 0;

    const cancel = () =>
        history.push(`/guests/checkin/${guest?.type || 'limited'}`);

    const openOverride = (e: any) => {
        if (e?.preventDefault) e.preventDefault();
        setOverrideValue(String(effectiveFeeValue));
        setOverrideOpen(true);
    };
    const applyOverride = () => {
        const parsed = parseInt(overrideValue, 10);
        if (Number.isNaN(parsed) || parsed < 0) return;
        setFeeOverride(parsed);
        setOverrideOpen(false);
    };

    const performCheckIn = async () => {
        if (guest?.type === 'allAccess') {
            // Server-side single source of truth — atomic check-in.
            await axios.post('/api/pink-fee/checkin', {
                guestID: guest.guestID,
                paymentMethod: 'cash',
            });
            return;
        }
        // Limited: legacy two-step. Use the previewed (or overridden)
        // fee so the displayed AMOUNT DUE matches the charge.
        await axios.post('/api/transactions', {
            guestID: guest.guestID,
            firstName: guest.firstName,
            lastName: guest.lastName,
            stageName: 'NONE',
            fee: 'EventFee',
            type: 'limited',
            value: effectiveFeeValue,
            points: 0,
        });
        await axios.patch(
            `/api/guests/${(guest as any)._id}?type=limited`,
            {
                lifeTimeFees: effectiveFeeValue,
                entryDates: [new Date()],
            }
        );
    };

    const handleAccept = async () => {
        if (submittingRef.current) return;
        setError('');

        if (age == null) {
            return setError(
                'Could not read date of birth. Please re-scan or fix the record.'
            );
        }
        if (isUnderage) {
            return setError(
                'Guest is under the legal entry age — cannot check in.'
            );
        }
        if (loadingFee || fee == null) {
            return setError('Fee is still loading. Try again in a moment.');
        }
        // Inactive All Access guests trigger the renewal modal — same
        // gate the previous form had, kept for the renewal upsell flow.
        if (
            guest?.type === 'allAccess' &&
            !guest.active &&
            !renewalStatus
        ) {
            return setRenewalOpen(true);
        }

        submittingRef.current = true;
        setSubmitting(true);
        try {
            await performCheckIn();
            // Best-effort email — don't block redirect on failure.
            sendEmailToGuest(guest, user).catch(() => undefined);
            setFlashName(stageOrName);
            window.setTimeout(
                () =>
                    history.push(
                        `/guests/checkin/${guest?.type || 'limited'}`
                    ),
                500
            );
        } catch (err: any) {
            setError(
                err?.response?.data?.message ||
                    err?.message ||
                    'Check-in failed. Try again or check the network.'
            );
            submittingRef.current = false;
        } finally {
            setSubmitting(false);
        }
    };

    // Accept gate — keep the same priority as the scan-new flow so
    // both confirm screens behave identically for staff.
    const feeReady = !loadingFee && fee != null;
    const acceptDisabled =
        submitting || isUnderage || age == null || !feeReady;
    const disabledReason: string | null = (() => {
        if (submitting) return 'Processing…';
        if (isUnderage) return 'Guest underage';
        if (age == null) return 'Date of birth unreadable — re-scan';
        if (!feeReady) return 'Loading fee…';
        return null;
    })();

    const containerStyle: React.CSSProperties = {
        width: '100%',
        maxWidth: 480,
        margin: '0 auto',
    };
    const buttonRowStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        alignItems: 'center',
        width: '100%',
        maxWidth: 320,
        margin: '12px auto 0',
    };
    const buttonWrapStyle: React.CSSProperties = {
        width: '100%',
        maxWidth: 280,
    };

    if (flashName) {
        return (
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: '#27ae60',
                    color: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    padding: 24,
                    textAlign: 'center',
                }}
            >
                <div
                    style={{
                        fontSize: 56,
                        fontWeight: 900,
                        letterSpacing: 2,
                        marginBottom: 16,
                    }}
                >
                    ✅ CHECK-IN COMPLETE
                </div>
                <div style={{ fontSize: 28, fontWeight: 700 }}>
                    Guest: {flashName}
                </div>
            </div>
        );
    }

    return (
        <FlexContainer>
            <div style={containerStyle}>
                <div
                    style={{
                        background: decision.bg,
                        color: '#fff',
                        padding: '22px 18px',
                        borderRadius: 14,
                        fontSize: 32,
                        fontWeight: 900,
                        textAlign: 'center',
                        marginBottom: 14,
                        border: `4px solid ${decision.border}`,
                        letterSpacing: 1.5,
                        lineHeight: 1.15,
                    }}
                >
                    <div>{decision.label}</div>
                    {decision.sub && (
                        <div
                            style={{
                                marginTop: 6,
                                fontSize: 16,
                                fontWeight: 700,
                                opacity: 0.95,
                            }}
                        >
                            {decision.sub}
                        </div>
                    )}
                </div>

                <div
                    style={{
                        background: '#1f1f1f',
                        border: '2px solid #444',
                        borderRadius: 12,
                        padding: 16,
                        marginBottom: 12,
                    }}
                >
                    <p style={{ fontSize: 18, margin: '2px 0' }}>
                        <strong>Guest:</strong>{' '}
                        <span style={{ color: '#fff' }}>{stageOrName}</span>
                    </p>
                    {dobDisplay && (
                        <p style={{ fontSize: 18, margin: '2px 0' }}>
                            <strong>DOB:</strong>{' '}
                            <span style={{ color: '#fff' }}>{dobDisplay}</span>
                        </p>
                    )}
                    <div
                        style={{
                            marginTop: 10,
                            padding: '10px 16px',
                            background: ageColor,
                            color: '#fff',
                            borderRadius: 8,
                            fontSize: 28,
                            fontWeight: 800,
                            letterSpacing: 1,
                            textAlign: 'center',
                        }}
                    >
                        AGE: {age == null ? '?' : age}
                    </div>
                    <div
                        style={{
                            marginTop: 8,
                            padding: '10px 16px',
                            background:
                                feeOverride != null ? '#b8860b' : '#2c3e50',
                            color: '#fff',
                            borderRadius: 8,
                            fontSize: 26,
                            fontWeight: 800,
                            letterSpacing: 1,
                            textAlign: 'center',
                        }}
                    >
                        AMOUNT DUE:{' '}
                        {loadingFee || fee == null
                            ? '…'
                            : `$${effectiveFeeValue}`}
                        {feeOverride != null && (
                            <span
                                style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    marginLeft: 8,
                                    opacity: 0.9,
                                }}
                            >
                                (overridden)
                            </span>
                        )}
                    </div>
                </div>

                {error !== '' && (
                    <div
                        style={{
                            background: '#c0392b',
                            color: '#fff',
                            padding: 10,
                            borderRadius: 6,
                            margin: '10px auto 0',
                            maxWidth: 420,
                            fontWeight: 700,
                            textAlign: 'center',
                            fontSize: 14,
                        }}
                    >
                        <p style={{ margin: 0 }}>{error}</p>
                    </div>
                )}

                <div style={buttonRowStyle}>
                    <div style={buttonWrapStyle}>
                        <Button
                            color={'green'}
                            margin={false}
                            disabled={acceptDisabled}
                            onClick={handleAccept}
                        >
                            Accept &amp; Check In
                        </Button>
                        {disabledReason && (
                            <div
                                style={{
                                    marginTop: 4,
                                    fontSize: 13,
                                    fontStyle: 'italic',
                                    color: submitting ? '#bbb' : '#e67e22',
                                    textAlign: 'center',
                                }}
                            >
                                {disabledReason}
                            </div>
                        )}
                    </div>
                    <div style={buttonWrapStyle}>
                        <Button
                            color={'yellow'}
                            margin={false}
                            onClick={openOverride}
                        >
                            Override Fee
                        </Button>
                    </div>
                    <div style={buttonWrapStyle}>
                        <Button
                            color={guest?.type || 'limited'}
                            margin={false}
                            onClick={cancel}
                        >
                            Cancel / Back
                        </Button>
                    </div>
                </div>

                <UpdateGuestModal
                    guest={guest}
                    setGuest={setGuest}
                    setRenewalStatus={setRenewalStatus}
                    isOpen={renewalOpen}
                    setIsOpen={setRenewalOpen}
                    feeType={feeType}
                    setGuestPoints={setGuestPoints}
                />
                <Modal
                    isOpen={overrideOpen}
                    onRequestClose={() => setOverrideOpen(false)}
                    ariaHideApp={false}
                    className="modal"
                >
                    <div>
                        <Header
                            title={'Override Fee'}
                            subtitle={'Enter a different fee amount'}
                        />
                        <div>
                            <span className="form-span">New fee:</span>
                            <input
                                type="number"
                                min={0}
                                className="form-input"
                                value={overrideValue}
                                onChange={(e) =>
                                    setOverrideValue(e.target.value)
                                }
                                autoFocus
                            />
                        </div>
                        <Button
                            color={'green'}
                            margin={true}
                            onClick={applyOverride}
                        >
                            Apply
                        </Button>
                        <Button
                            color={'red'}
                            margin={true}
                            onClick={(e: any) => {
                                e.preventDefault();
                                setOverrideOpen(false);
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                </Modal>
            </div>
        </FlexContainer>
    );
};

export default UpdateGuestForm;
